import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowLeft, Calendar, Users, Building2, Stethoscope, FlaskConical, FolderOpen, ClipboardList, Heart, User, Phone, Edit3 } from "lucide-react";

const TABS = [
  { id: "overview", label: "Visão geral", icon: User },
  { id: "agenda", label: "Agenda", icon: Calendar },
  { id: "professionals", label: "Profissionais", icon: Stethoscope },
  { id: "clinics", label: "Clínicas", icon: Building2 },
  { id: "attendances", label: "Atendimentos", icon: ClipboardList },
  { id: "exams", label: "Exames", icon: FlaskConical },
  { id: "documents", label: "Documentos", icon: FolderOpen },
  { id: "history", label: "Histórico", icon: ClipboardList },
  { id: "care_plan", label: "Plano de cuidados", icon: Heart },
];

const ageOf = (birth) => {
  if (!birth) return "—";
  const b = new Date(birth); const now = new Date();
  let a = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) a--;
  return a + " anos";
};

export default function FPBeneficiaryProfile() {
  const { id } = useParams();
  const [tab, setTab] = useState("overview");
  const [ov, setOv] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/fp/beneficiaries/${id}/overview`);
        setOv(data);
      } finally { setLoading(false); }
    })();
  }, [id]);

  useEffect(() => {
    if (tab === "history") {
      api.get(`/fp/history/${id}`).then((r) => setHistory(r.data)).catch(() => {});
    }
  }, [tab, id]);

  if (loading) return <div className="p-10 text-slate-500">Carregando…</div>;
  if (!ov) return <div className="p-10 text-red-500">Não encontrado</div>;
  const b = ov.beneficiary;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="fp-benef-profile">
      <Link to="/filho-presente/familiares" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0B2B62] mb-4">
        <ArrowLeft className="w-4 h-4" /> Meus familiares
      </Link>

      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6">
        <div className="flex items-start gap-4 flex-wrap">
          {b.photo_url ? (
            <img src={b.photo_url} alt="" className="w-20 h-20 rounded-full object-cover" />
          ) : (
            <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-2xl font-bold">
              {(b.name || "?").charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0B2B62]">{b.name}</h1>
            <div className="text-slate-600 text-sm mt-1">
              {ageOf(b.birth_date)}{b.city && ` · ${b.city}${b.state ? "/" + b.state : ""}`}{b.relation_to_owner && ` · ${b.relation_to_owner}`}
            </div>
            {b.emergency_contact?.name && (
              <div className="inline-flex items-center gap-1.5 text-xs text-slate-500 mt-2">
                <Phone className="w-3.5 h-3.5" /> Emergência: {b.emergency_contact.name} · {b.emergency_contact.phone}
              </div>
            )}
          </div>
          <Link to={`/filho-presente/familiares/${id}/editar`} data-testid="fp-edit-beneficiary"
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#0B2B62] px-3 py-2 rounded-md hover:bg-slate-50">
            <Edit3 className="w-4 h-4" /> Editar
          </Link>
        </div>

        <div className="flex gap-2 mt-6 flex-wrap">
          <Link to={`/filho-presente/solicitar-consulta?beneficiary_id=${id}`}
            data-testid="fp-quick-consulta"
            className="text-xs sm:text-sm bg-[#0B2B62] text-white px-3 py-2 rounded-lg hover:bg-slate-800">
            + Consulta
          </Link>
          <Link to={`/filho-presente/solicitar-exame?beneficiary_id=${id}`}
            className="text-xs sm:text-sm bg-white border border-slate-300 text-slate-700 px-3 py-2 rounded-lg hover:border-[#0B2B62]">
            + Exame
          </Link>
          <Link to={`/filho-presente/solicitar-transporte?beneficiary_id=${id}`}
            className="text-xs sm:text-sm bg-white border border-slate-300 text-slate-700 px-3 py-2 rounded-lg hover:border-[#0B2B62]">
            + Transporte
          </Link>
          <Link to={`/filho-presente/solicitar-acompanhante?beneficiary_id=${id}`}
            data-testid="fp-quick-acompanhante"
            className="text-xs sm:text-sm bg-white border border-slate-300 text-slate-700 px-3 py-2 rounded-lg hover:border-[#0B2B62]">
            + Acompanhante
          </Link>
          <Link to={`/filho-presente/familiares/${id}/documentos`}
            data-testid="fp-quick-docs"
            className="text-xs sm:text-sm bg-white border border-slate-300 text-slate-700 px-3 py-2 rounded-lg hover:border-[#0B2B62]">
            📎 Documentos
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-6 overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`fp-tab-${t.id}`}
              className={`px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                tab === t.id ? "border-[#0B2B62] text-[#0B2B62]" : "border-transparent text-slate-500 hover:text-slate-800"
              }`}>
              <t.icon className="w-3.5 h-3.5 inline mr-1.5" />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      {tab === "overview" && (
        <div className="grid md:grid-cols-2 gap-6">
          <Card title="Próximos compromissos" empty="Nada agendado por enquanto">
            {ov.upcoming?.slice(0, 5).map((r) => (
              <RequestRow key={r.id} r={r} />
            ))}
          </Card>
          <Card title="Solicitações abertas" empty="Nenhuma solicitação em aberto">
            {ov.open_requests?.slice(0, 5).map((r) => (
              <RequestRow key={r.id} r={r} />
            ))}
          </Card>
          <Card title="Últimos atendimentos" empty="Sem atendimentos ainda">
            {ov.last_attendances?.slice(0, 5).map((r) => (
              <RequestRow key={r.id} r={r} />
            ))}
          </Card>
          <Card title="Profissionais vinculados" empty="Nenhum profissional vinculado ainda">
            {ov.linked_professionals?.map((p) => (
              <Link key={p.id} to={`/filho-presente/profissionais/${p.id}`}
                className="flex items-center gap-2 py-2 hover:text-[#0B2B62]">
                {p.photo_url ? <img src={p.photo_url} className="w-8 h-8 rounded-full" alt="" /> :
                  <div className="w-8 h-8 rounded-full bg-slate-200" />}
                <div className="text-sm">
                  <div className="font-medium">{p.display_name}</div>
                  <div className="text-xs text-slate-500">{p.category}</div>
                </div>
              </Link>
            ))}
          </Card>
        </div>
      )}

      {tab === "agenda" && <FilteredRequests bid={id} filter={(r) => ["confirmada","aguardando_profissional","solicitada"].includes(r.status)} empty="Sem compromissos futuros" />}
      {tab === "attendances" && <FilteredRequests bid={id} filter={(r) => r.type === "consulta"} empty="Nenhuma consulta registrada" />}
      {tab === "exams" && <FilteredRequests bid={id} filter={(r) => r.type === "exame"} empty="Nenhum exame registrado" />}
      {tab === "professionals" && (
        <div className="text-sm text-slate-500 bg-white border border-slate-200 rounded-xl p-6 text-center">
          Os profissionais vinculados aparecem automaticamente conforme as consultas confirmadas.
        </div>
      )}
      {tab === "clinics" && (
        <div className="text-sm text-slate-500 bg-white border border-slate-200 rounded-xl p-6 text-center">
          As clínicas parceiras aparecem conforme atendimentos realizados.
        </div>
      )}
      {tab === "documents" && <DocumentsTab bid={id} />}
      {tab === "history" && (
        <div className="bg-white border border-slate-200 rounded-xl divide-y">
          {history.length === 0 ? <div className="p-6 text-sm text-slate-500 text-center">Sem eventos registrados ainda.</div>
            : history.map((h) => (
              <div key={h.id} className="p-4 flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-[#0B2B62] mt-2 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800">{h.event_label}</div>
                  <div className="text-xs text-slate-500">
                    {new Date(h.at).toLocaleString("pt-BR")} · por {h.by_name || "sistema"}
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}
      {tab === "care_plan" && <CarePlanTab bid={id} />}
    </div>
  );
}

const Card = ({ title, children, empty }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5">
    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-3">{title}</div>
    <div className="divide-y">
      {React.Children.count(children) === 0 || !children || (Array.isArray(children) && children.every((c) => !c))
        ? <div className="text-sm text-slate-500 py-2">{empty}</div>
        : children}
    </div>
  </div>
);

const RequestRow = ({ r }) => (
  <Link to={`/filho-presente/solicitacoes/${r.id}`} className="flex items-center justify-between py-2 hover:text-[#0B2B62]">
    <div className="min-w-0">
      <div className="text-sm font-medium truncate">
        {r.type === "consulta" ? `Consulta · ${r.specialty || "—"}` : r.type === "exame" ? `Exame · ${r.exam_type}` : r.type}
      </div>
      <div className="text-xs text-slate-500">{r.desired_date || "sem data"}</div>
    </div>
    <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{r.status?.replace("_", " ")}</span>
  </Link>
);

const FilteredRequests = ({ bid, filter, empty }) => {
  const [reqs, setReqs] = useState([]);
  useEffect(() => {
    api.get(`/fp/requests?beneficiary_id=${bid}`).then((r) => setReqs(r.data.filter(filter))).catch(() => {});
    // eslint-disable-next-line
  }, [bid]);
  return (
    <div className="bg-white border border-slate-200 rounded-xl divide-y">
      {reqs.length === 0 ? <div className="p-6 text-sm text-slate-500 text-center">{empty}</div>
        : reqs.map((r) => <div key={r.id} className="p-3"><RequestRow r={r} /></div>)}
    </div>
  );
};

const DocumentsTab = ({ bid }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/fp/documents/${bid}`).then((r) => setDocs(r.data)).finally(() => setLoading(false));
  }, [bid]);
  if (loading) return <div className="p-6 text-slate-500 text-sm">Carregando…</div>;
  return (
    <div className="bg-white border border-slate-200 rounded-xl">
      {docs.length === 0 ? (
        <div className="p-6 text-center">
          <div className="text-slate-700 mb-3">Nenhum documento anexado ainda.</div>
          <Link to={`/filho-presente/familiares/${bid}/documentos`}
            className="inline-block bg-[#0B2B62] text-white px-4 py-2 rounded-lg text-sm">Enviar documentos</Link>
        </div>
      ) : (
        <>
          <div className="divide-y">
            {docs.slice(0, 8).map((d) => (
              <div key={d.id} className="p-3 flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{d.title || d.original_filename}</div>
                  <div className="text-xs text-slate-500">{new Date(d.created_at).toLocaleDateString("pt-BR")}</div>
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${d.visibility === "private" ? "bg-slate-100 text-slate-600" : "bg-emerald-50 text-emerald-700"}`}>
                  {d.visibility === "private" ? "Privado" : "Compartilhado"}
                </span>
              </div>
            ))}
          </div>
          <div className="p-4 border-t text-right">
            <Link to={`/filho-presente/familiares/${bid}/documentos`}
              className="text-sm text-[#0B2B62] hover:underline">Gerenciar documentos →</Link>
          </div>
        </>
      )}
    </div>
  );
};

const CarePlanTab = ({ bid }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/fp/care-plan/${bid}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, [bid]);
  if (loading) return <div className="p-6 text-slate-500 text-sm">Carregando…</div>;
  return (
    <div className="bg-white border border-slate-200 rounded-xl">
      {items.length === 0 ? (
        <div className="p-6 text-center">
          <div className="text-slate-700 mb-3">Nenhuma rotina cadastrada ainda.</div>
          <Link to={`/filho-presente/plano-cuidados?beneficiary_id=${bid}`}
            className="inline-block bg-[#0B2B62] text-white px-4 py-2 rounded-lg text-sm">Organizar rotina</Link>
        </div>
      ) : (
        <>
          <div className="divide-y">
            {items.map((it) => (
              <div key={it.id} className="p-4 flex items-start gap-3">
                <div className="w-1 self-stretch bg-amber-400 rounded" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-800">{it.activity}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    <span className="text-amber-700 font-medium">{it.frequency_label}</span>
                    {it.next_at && <> · próximo: <span className="font-medium">{new Date(it.next_at + "T00:00").toLocaleDateString("pt-BR")}</span></>}
                    {it.responsible && <> · com {it.responsible}</>}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="p-4 border-t text-right">
            <Link to={`/filho-presente/plano-cuidados?beneficiary_id=${bid}`}
              className="text-sm text-[#0B2B62] hover:underline">Gerenciar rotinas →</Link>
          </div>
        </>
      )}
    </div>
  );
};
